import { useState } from 'react';
import { api } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { fmtMoney } from '../lib/format';
import { buildCategoriaOptions, catPathSugerida } from '../lib/ui';
import { useToast } from '../components/Toast';
import type { PageProps } from '../App';
import type { ContaAPagar, Comprovante } from '../types';

export function Fila({ navigate, reloadFila }: PageProps) {
  const toast = useToast();
  const plano = useFetch(() => api.listPlano(), []);
  const catOptions = plano.data ? buildCategoriaOptions(plano.data.items) : [];

  // Classificações: sugeridas + pendentes (sem sugestão)
  const classif = useFetch(async () => {
    const [sug, pen] = await Promise.all([
      api.listContas({ classificacao_status: 'sugerida', limit: 100 }),
      api.listContas({ classificacao_status: 'pendente', limit: 100 }),
    ]);
    return [...sug.items, ...pen.items];
  }, []);

  // Diferenças de pagamento: comprovantes pending com conta vinculada
  const difs = useFetch(async () => {
    const comps = (await api.listComprovantes({ match_status: 'pending', limit: 100 })).items
      .filter((c) => c.contas_a_pagar_id);
    const contas = await Promise.all(comps.map((c) => api.getConta(c.contas_a_pagar_id!).catch(() => null)));
    return comps.map((comp, i) => ({ comp, conta: contas[i] })).filter((x) => x.conta) as { comp: Comprovante; conta: ContaAPagar }[];
  }, []);

  const reload = () => { classif.refetch(); difs.refetch(); reloadFila(); };

  return (
    <div className="page narrow" style={{ maxWidth: 1100 }}>
      <h1>A Confirmar</h1>
      <div className="subtitle">Revisão de 1 clique do que a IA preparou. Confirmar também ensina o padrão do fornecedor.</div>

      <div className="section-title">Classificações sugeridas <span className="count">· {classif.data?.length ?? 0}</span></div>
      {classif.loading ? (
        <div className="loading">Carregando…</div>
      ) : (classif.data?.length ?? 0) === 0 ? (
        <div className="empty" style={{ marginBottom: 24 }}>Nenhuma classificação aguardando — tudo em dia.</div>
      ) : (
        <div className="stack" style={{ marginBottom: 28 }}>
          {classif.data!.map((c) => (
            <ClassifCard key={c.id} conta={c} catOptions={catOptions} navigate={navigate}
              onDone={reload} />
          ))}
        </div>
      )}

      <div className="section-title">Diferenças de pagamento <span className="count">· {difs.data?.length ?? 0}</span></div>
      {difs.loading ? (
        <div className="loading">Carregando…</div>
      ) : (difs.data?.length ?? 0) === 0 ? (
        <div className="empty">Nenhum comprovante aguardando confirmação.</div>
      ) : (
        <div className="stack">
          {difs.data!.map(({ comp, conta }) => (
            <DifCard key={comp.id} comp={comp} conta={conta} navigate={navigate}
              onDone={() => { reload(); toast('Pagamento confirmado e comprovante vinculado.'); }}
              onError={(m) => toast(m, 'err')} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClassifCard({ conta, catOptions, navigate, onDone }:
  { conta: ContaAPagar; catOptions: { id: number; label: string }[]; navigate: PageProps['navigate']; onDone: () => void }) {
  const toast = useToast();
  const temSugestao = conta.classificacao_status === 'sugerida' && !!conta.classificacao_sugerida_id;
  const [sel, setSel] = useState<number | ''>(temSugestao ? conta.classificacao_sugerida_id! : '');
  const [padrao, setPadrao] = useState(true);
  const [busy, setBusy] = useState(false);

  const confirmar = async () => {
    if (!sel) { toast('Escolha uma categoria.', 'err'); return; }
    setBusy(true);
    try {
      await api.classificarConta(conta.id, Number(sel), padrao);
      toast('Classificação confirmada.' + (padrao ? ' Padrão do fornecedor atualizado.' : ''));
      onDone();
    } catch {
      toast('Falha ao confirmar classificação.', 'err');
      setBusy(false);
    }
  };

  return (
    <div className="card pad-lg" style={{ padding: '16px 18px' }}>
      <div className="row wrap">
        <div style={{ flex: 1, minWidth: 240 }}>
          <div className="row" style={{ gap: 8 }}>
            <a onClick={() => navigate('detalhe', { id: conta.id })} style={{ fontSize: 14, fontWeight: 600 }}>{conta.fornecedor}</a>
            {conta.origem && <span className="origem">{conta.origem}</span>}
          </div>
          <div className="text-sm" style={{ color: 'var(--muted-2)', marginTop: 2 }}>{conta.descricao ?? '—'}</div>
        </div>
        <div className="mono" style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{fmtMoney(conta.valor)}</div>
      </div>
      <div className="row wrap" style={{ gap: 12, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-soft)' }}>
        {temSugestao ? (
          <>
            <span className="chip-sq suggest">IA sugere: {catPathSugerida(conta) || conta.categoria_sugerida}</span>
            {conta.classificacao_confianca != null && (
              <span className="text-sm muted">{Math.round(conta.classificacao_confianca * 100)}% conf.</span>
            )}
          </>
        ) : (
          <span className="chip-sq neutral">Sem sugestão — classifique manualmente</span>
        )}
        <select className="select sm" style={{ width: 'auto', maxWidth: 340 }} value={sel} onChange={(e) => setSel(e.target.value ? Number(e.target.value) : '')}>
          <option value="">Escolher categoria…</option>
          {catOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <label className="checkline">
          <input type="checkbox" checked={padrao} onChange={(e) => setPadrao(e.target.checked)} />
          definir como padrão do fornecedor
        </label>
        <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={confirmar} disabled={busy}>✓ Confirmar</button>
      </div>
    </div>
  );
}

function DifCard({ comp, conta, navigate, onDone, onError }:
  { comp: Comprovante; conta: ContaAPagar; navigate: PageProps['navigate']; onDone: () => void; onError: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const sug = comp.raw_extracted?._sugestao_diferenca;
  const liq = conta.valor_liquido ?? conta.valor ?? 0;
  const dif = comp.valor - liq;
  const difPos = dif >= 0;

  const confirmar = async () => {
    setBusy(true);
    try {
      await api.pagarConta(conta.id, {
        pago_em: comp.data_pagamento,
        juros: sug?.juros ?? (difPos ? Math.abs(dif) : 0),
        desconto: sug?.desconto ?? (!difPos ? Math.abs(dif) : 0),
        valor_pago: comp.valor,
      });
      await api.vincularComprovante(comp.id, conta.id);
      onDone();
    } catch (e) {
      const body = (e as { body?: { error?: string; esperado?: number } }).body;
      onError(body?.error === 'valores_inconsistentes'
        ? `Valores não fecham (esperado ${fmtMoney(body.esperado ?? 0)}).`
        : 'Falha ao confirmar pagamento.');
      setBusy(false);
    }
  };

  const btnLabel = sug?.juros ? `Confirmar com juros ${fmtMoney(sug.juros)}`
    : sug?.desconto ? `Confirmar com desconto ${fmtMoney(sug.desconto)}`
    : 'Confirmar pagamento';

  return (
    <div className="card" style={{ padding: '16px 18px' }}>
      <div className="row wrap" style={{ gap: 14 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <a onClick={() => navigate('detalhe', { id: conta.id })} style={{ fontSize: 14, fontWeight: 600 }}>{conta.fornecedor}</a>
          <div className="text-sm" style={{ color: 'var(--muted-2)', marginTop: 2 }}>{conta.descricao ?? '—'}</div>
        </div>
        <div className="right"><div className="text-sm muted">Conta (líquido)</div><div className="mono cell-strong">{fmtMoney(liq)}</div></div>
        <div className="right"><div className="text-sm muted">Comprovante</div><div className="mono cell-strong">{fmtMoney(comp.valor)}</div></div>
        <div className="right"><div className="text-sm muted">Diferença</div>
          <div className="mono cell-strong" style={{ color: difPos ? 'var(--danger)' : 'var(--green)' }}>{difPos ? '+' : ''}{fmtMoney(dif)}</div>
        </div>
      </div>
      <div className="row" style={{ gap: 12, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-soft)' }}>
        {sug?.motivo && <span className="chip-sq info">{sug.motivo}</span>}
        <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={confirmar} disabled={busy}>{btnLabel}</button>
      </div>
    </div>
  );
}
